import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import logo from '../../assets/logo-getit.png';
import logoPosgo from '../../assets/logo-posgo.png';
import ConfirmCancelModal from '../shared/ConfirmCancelModal';

const PROXY_URL = 'http://localhost:9000';

const getMimeType = (b64) => b64?.startsWith('iVBOR') ? 'image/png' : 'image/jpeg';

const ProductImage = ({ item }) => {
    const [src, setSrc] = useState(null);

    useEffect(() => {
        if (item.image_base64) {
            setSrc(`data:${getMimeType(item.image_base64)};base64,${item.image_base64}`);
            return;
        }
        const productId = item.original_id || item.id.toString().split('-custom-')[0];
        if (productId) {
            setSrc(`${PROXY_URL}/images/products/${productId}`);
        }
    }, [item]);

    return (
        <div className="product-image-container-fixed">
            {src ? (
                <img src={src} alt={item.name} className="product-image-fixed" />
            ) : (
                <div className="image-placeholder-fixed">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
                    </svg>
                </div>
            )}
        </div>
    );
};

const PaymentScreen = () => {
    const { total, items, updateQuantity, removeItem, clearCart } = useCart();
    const navigate = useNavigate();
    const [isAnimating, setIsAnimating] = useState(false);
    const [showAnimation, setShowAnimation] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    useEffect(() => {
        setTimeout(() => setShowAnimation(true), 50);
    }, []);

    const ivaAmount = total * 0.19;
    const subtotalWithoutTax = total - ivaAmount;

    const handleProceedToPayment = () => {
        if (items.length === 0) {
            alert('Tu carrito está vacío. Agrega productos para proceder al pago.');
            return;
        }

        setIsAnimating(true);
        setTimeout(() => {
            navigate('/payment-method');
        }, 300);
    };

    const handleBackToProducts = () => {
        setIsAnimating(true);
        setTimeout(() => {
            navigate('/', { replace: true });
        }, 300);
    };

    return (
        <>
        <div className="payment-container-fixed" style={{ backgroundColor: '#f5f5f5' }}>
            <div className="payment-header-fixed">
                <button
                    onClick={handleBackToProducts}
                    className="back-button-fixed"
                    disabled={isAnimating}
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6" />
                    </svg>
                </button>

                <img src={logo} alt="GetIT" className="header-logo-payment" />

                <div className="header-right-group">
                    <div className="posgo-badge">
                        <img src={logoPosgo} alt="PosGo" className="posgo-badge-logo" />
                        <span className="posgo-badge-text">PosGo.cl</span>
                    </div>
                </div>
            </div>

            <div className="products-area-fixed">
                <div className="items-count-fixed">
                    {items.reduce((sum, item) => sum + item.quantity, 0)} PRODUCTOS EN TU PEDIDO
                </div>
                {items.map((item, index) => (
                    <div
                        key={item.id}
                        className="product-item-fixed"
                        style={{ animationDelay: `${index * 100}ms` }}
                    >
                        <ProductImage item={item} />

                        <div className="product-info-fixed">
                            <h3 className="product-name-fixed">{item.name}</h3>

                            {item.customDescription && (
                                <div className="customization-fixed">
                                    <span className="customization-label">+</span>
                                    <span className="customization-text">{item.customDescription}</span>
                                </div>
                            )}
                        </div>

                        <div className="product-right">
                            <div className="qty-controls">
                                <button
                                    className="qty-btn"
                                    onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                >
                                    −
                                </button>
                                <span className="qty-value">{item.quantity}</span>
                                <button
                                    className="qty-btn"
                                    onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                >
                                    +
                                </button>
                            </div>

                            <div className="product-price-fixed">
                                <p className="unit-price">${item.price.toLocaleString()} c/u</p>
                                <p className="total-price">${(item.price * item.quantity).toLocaleString()}</p>
                            </div>

                            <button
                                className="remove-btn"
                                onClick={() => removeItem(item.id)}
                            >
                                <svg width="26" height="26" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />
                                </svg>
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            <div className="summary-area">
                <div className="price-summary-fixed">
                    <div className="summary-row-fixed">
                        <span className="summary-label-fixed">Subtotal (Neto):</span>
                        <span className="summary-value-fixed">${Math.round(subtotalWithoutTax).toLocaleString()}</span>
                    </div>
                    <div className="summary-row-fixed">
                        <span className="summary-label-fixed">IVA (19%):</span>
                        <span className="summary-value-fixed">${Math.round(ivaAmount).toLocaleString()}</span>
                    </div>
                    <div className="total-row-fixed">
                        <span className="total-label-fixed">Total a Pagar:</span>
                        <span className="total-value-fixed">${total.toLocaleString()}</span>
                    </div>
                </div>

                <div className="action-buttons-fixed">
                    <button className="back-menu-button-fixed" onClick={handleBackToProducts}>
                        <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                        VOLVER AL MENÚ
                    </button>
                    {items.length > 0 && (
                        <div className="right-buttons-group">
                            <button className="cancel-all-button" onClick={() => setShowConfirm(true)}>
                                CANCELAR TODO
                            </button>
                            <button className="proceed-button-fixed" onClick={handleProceedToPayment}>
                                PROCEDER AL PAGO
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <style jsx>{`
                .payment-container-fixed {
                    height: 100vh;
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                    opacity: ${showAnimation && !isAnimating ? 1 : 0};
                    transform: ${showAnimation && !isAnimating ? 'translateY(0)' : 'translateY(20px)'};
                    transition: all 400ms cubic-bezier(0.4, 0, 0.2, 1);
                    position: relative;
                }

                .payment-header-fixed {
                    background: white;
                    padding: 1.5rem;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.07);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    position: relative;
                    z-index: 2;
                }

                .back-button-fixed {
                    position: absolute;
                    left: 1.5rem;
                    width: 56px;
                    height: 56px;
                    border: none;
                    background: #f0f0f0;
                    border-radius: 16px;
                    color: #333;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 200ms ease;
                }

                .back-button-fixed:hover {
                    background: #e0e0e0;
                    transform: translateX(-2px);
                }

                .back-button-fixed svg {
                    width: 32px;
                    height: 32px;
                }

                .header-logo-payment {
                    height: 100px;
                    width: auto;
                    object-fit: contain;
                }

                .header-text-fixed {
                    flex: 1;
                }

                .payment-title-fixed {
                    color: #1f2937;
                    font-size: 1.75rem;
                    font-weight: 800;
                    margin: 0 0 0.25rem 0;
                    letter-spacing: -0.5px;
                }

                .payment-subtitle-fixed {
                    color: #6b7280;
                    font-size: 1rem;
                    margin: 0;
                    font-weight: 500;
                }

                .header-right-group {
                    position: absolute;
                    right: 1.5rem;
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                }

                .posgo-badge {
                    display: flex;
                    align-items: center;
                    gap: 0.4rem;
                }

                .posgo-badge-logo {
                    height: 80px;
                    width: auto;
                }

                .posgo-badge-text {
                    font-size: 1.5rem;
                    font-weight: 700;
                    color: #db2113;
                    font-family: Quicksand, sans-serif;
                }

                .items-count-fixed {
                    color: #6b7280;
                    font-weight: 700;
                    font-size: 1rem;
                    letter-spacing: 0.5px;
                    margin-bottom: 1rem;
                    text-transform: uppercase;
                }

                .products-area-fixed {
                    flex: 1;
                    padding: 2rem 1.5rem;
                    max-width: 1200px;
                    margin: 0 auto;
                    width: 100%;
                    overflow-y: auto;
                    min-height: 0;
                }

                .product-item-fixed {
                    background: white;
                    padding: 1.5rem;
                    border-radius: 16px;
                    margin-bottom: 1.25rem;
                    display: flex;
                    align-items: center;
                    gap: 1.5rem;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
                    animation: slideInUp 300ms ease-out both;
                    border: 2px solid #e5e7eb;
                }

                .product-image-container-fixed {
                    width: 120px;
                    height: 120px;
                    flex-shrink: 0;
                    border-radius: 12px;
                    overflow: hidden;
                    background: transparent;
                    position: relative;
                }

                .product-image-fixed {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }

                .image-placeholder-fixed {
                    width: 100%;
                    height: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: transparent;
                    color: #9ca3af;
                }

                .image-placeholder-fixed svg {
                    width: 48px;
                    height: 48px;
                }

                .product-info-fixed {
                    flex: 1;
                    min-width: 0;
                }

                .product-name-fixed {
                    font-size: 1.5rem;
                    font-weight: 700;
                    color: #1f2937;
                    margin: 0 0 0.75rem 0;
                }

                .customization-fixed {
                    display: flex;
                    align-items: baseline;
                    gap: 0.3rem;
                    margin-top: 0.3rem;
                }

                .customization-label {
                    font-size: 0.85rem;
                    font-weight: 700;
                    color: #0E8164;
                    flex-shrink: 0;
                }

                .customization-text {
                    font-size: 0.85rem;
                    color: #6b7280;
                    font-weight: 500;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .product-right {
                    display: flex;
                    align-items: center;
                    gap: 1.5rem;
                    flex-shrink: 0;
                }

                .qty-controls {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    background: transparent;
                    padding: 0.6rem 1rem;
                    border-radius: 14px;
                }

                .qty-btn {
                    width: 48px;
                    height: 48px;
                    border: none;
                    border-radius: 50%;
                    background: #0E8164;
                    color: white;
                    font-size: 1.5rem;
                    font-weight: 700;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: background 0.2s;
                    line-height: 1;
                }

                .qty-btn:hover {
                    background: #0a6b52;
                }

                .qty-value {
                    min-width: 36px;
                    text-align: center;
                    font-size: 1.6rem;
                    font-weight: 700;
                    color: #1f2937;
                }

                .product-price-fixed {
                    text-align: right;
                    min-width: 110px;
                }

                .unit-price {
                    font-size: 0.875rem;
                    color: #9ca3af;
                    margin: 0 0 0.25rem 0;
                }

                .total-price {
                    font-size: 1.75rem;
                    font-weight: 700;
                    color: #0E8164;
                    margin: 0;
                }

                .remove-btn {
                    width: 52px;
                    height: 52px;
                    border: none;
                    border-radius: 50%;
                    background: #fee2e2;
                    color: #DC2626;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: background 0.2s;
                    flex-shrink: 0;
                }

                .remove-btn:hover {
                    background: #fecaca;
                }

                .summary-area {
                    padding: 1.5rem;
                    max-width: 1200px;
                    margin: 0 auto;
                    width: 100%;
                    position: relative;
                    z-index: 1;
                }

                .price-summary-fixed {
                    background: white;
                    padding: 1.5rem;
                    border-radius: 16px;
                    margin-bottom: 1.5rem;
                    border: 2px solid #0E8164;
                }

                .summary-row-fixed {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 0.875rem;
                    font-size: 1.125rem;
                }

                .summary-label-fixed {
                    color: #6b7280;
                    font-weight: 600;
                }

                .summary-value-fixed {
                    font-weight: 700;
                    color: #374151;
                }

                .total-row-fixed {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    border-top: 3px solid #0E8164;
                    margin-top: 1.25rem;
                    padding-top: 1.25rem;
                }

                .total-label-fixed {
                    font-size: 1.5rem;
                    font-weight: 800;
                    color: #1f2937;
                }

                .total-value-fixed {
                    font-size: 2.75rem;
                    font-weight: 900;
                    color: #0E8164;
                }

                .action-buttons-fixed {
                    display: flex;
                    gap: 1rem;
                    align-items: center;
                }

                .right-buttons-group {
                    display: flex;
                    align-items: center;
                    gap: 1.5rem;
                    flex: 2;
                }

                .cancel-all-button {
                    padding: 0.875rem 1.25rem;
                    border: 2px solid #DC2626;
                    border-radius: 14px;
                    font-size: 0.95rem;
                    font-weight: 700;
                    cursor: pointer;
                    background: white;
                    color: #DC2626;
                    white-space: nowrap;
                    transition: all 200ms ease;
                    flex-shrink: 0;
                }

                .cancel-all-button:hover {
                    background: #fee2e2;
                }

                .back-menu-button-fixed {
                    flex: 1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                    padding: 1.125rem 2rem;
                    border: none;
                    border-radius: 16px;
                    font-size: 1.125rem;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 200ms ease;
                    background: #6b7280;
                    color: white;
                }

                .back-menu-button-fixed:hover {
                    background: #4b5563;
                }

                .proceed-button-fixed {
                    flex: 1;
                    padding: 1.125rem 2rem;
                    border: none;
                    border-radius: 16px;
                    font-size: 1.25rem;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 200ms ease;
                    background: #0E8164;
                    color: white;
                }

                .proceed-button-fixed:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(14, 129, 100, 0.3);
                }

                @keyframes slideInUp {
                    0% { opacity: 0; transform: translateY(20px); }
                    100% { opacity: 1; transform: translateY(0); }
                }

                @media (max-width: 768px) {
                    .product-item-fixed {
                        flex-direction: column;
                        text-align: center;
                    }

                    .action-buttons-fixed {
                        flex-direction: column;
                    }

                    .product-right {
                        width: 100%;
                        justify-content: space-between;
                    }
                }
            `}</style>
        </div>

        <ConfirmCancelModal
            isOpen={showConfirm}
            onConfirm={() => { setShowConfirm(false); clearCart(); handleBackToProducts(); }}
            onCancel={() => setShowConfirm(false)}
        />
        </>
    );
};

export default PaymentScreen;